import { Alert, Form, Input, Modal, Typography, message } from "antd";
import PasswordRequirements from "components/password_requirements";
import { useStore } from "contexts";
import { FC, useEffect, useState } from "react";
import staff from "services/admin/users/staff";
import { PASSWORD_HINT, passwordRule } from "utils/password";
import useRequest from "utils/useRequest";

interface FormValues {
  current_password: string;
  password: string;
  confirm: string;
}

/**
 * Blocking, non-dismissable modal that forces a signed-in staff member to
 * rotate the one-time password mailed to them before they can use the app.
 * It reads the must-change signal straight from the current user in the store
 * (populated by both `/admin/auth/login` and `/admin/auth/info`), so it works
 * whether the flag arrived at login or on a later session refresh. There is no
 * way out but a successful change — no cancel, no mask click, no Esc.
 */
const ForceChangePassword: FC = () => {
  const [{ authorized, auth }, , { info }] = useStore();
  const [form] = Form.useForm<FormValues>();
  const password = Form.useWatch("password", form) ?? "";

  const mustChange =
    !!auth?.must_change_password || auth?.next_step === "change_password";
  const open = authorized && mustChange;

  // Clear any half-typed values whenever the gate closes so a re-open starts
  // fresh (e.g. after a session refresh re-raises the flag).
  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const change = useRequest(staff.changeOwnPassword, {
    manual: true,
    onSuccess: () => {
      message.success("Нууц үг шинэчлэгдлээ");
      form.resetFields();
      // Re-fetch the current user so `must_change_password` clears and the
      // gate lifts.
      info();
    },
    onError: (err) => message.error(err.message),
  });

  return (
    <Modal
      open={open}
      title="Нууц үг солих шаардлагатай"
      okText="Хадгалах"
      closable={false}
      maskClosable={false}
      keyboard={false}
      cancelButtonProps={{ style: { display: "none" } }}
      confirmLoading={change.loading}
      onOk={() => form.submit()}
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Танд түр нууц үг олгогдсон байна. Үргэлжлүүлэхийн тулд шинэ нууц үгээ
        заавал тохируулна уу.
      </Typography.Paragraph>
      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 16 }}
        message={PASSWORD_HINT}
      />
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={(values) =>
          change
            .run({
              current_password: values.current_password,
              password: values.password,
            })
            .catch(() => undefined)
        }
      >
        <Form.Item
          name="current_password"
          label="Одоогийн нууц үг"
          rules={[{ required: true, message: "Одоогийн нууц үгээ оруулна уу" }]}
        >
          <Input.Password autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          name="password"
          label="Шинэ нууц үг"
          dependencies={["current_password"]}
          rules={[
            { required: true, message: "Шинэ нууц үгээ оруулна уу" },
            passwordRule({
              username: auth?.email,
              email: auth?.email,
              phone: auth?.phone,
              code: auth?.code,
            }),
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || value !== getFieldValue("current_password")
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Шинэ нууц үг өмнөхөөсөө өөр байх ёстой"),
                    ),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <PasswordRequirements value={password} style={{ marginBottom: 16 }} />
        <Form.Item
          name="confirm"
          label="Нууц үг давтах"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Нууц үгээ давтан оруулна уу" },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || value === getFieldValue("password")
                  ? Promise.resolve()
                  : Promise.reject(new Error("Нууц үг таарахгүй байна")),
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ForceChangePassword;
